import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { Plus } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PageContainer from "../../common_components/PageContainer";
import PagesCommonHeader from "../../common_components/PagesCommonHeader";
import CommonTable from "../../common_components/CommonTable";
import Pagination from "../../common_components/Pagination";
import AppButton from "../../common_components/AppButton";
import ConfirmActionModal from "../../common_components/ConfirmActionModal";
import HoverTooltip from "../../common_components/hoverOverlay/HoverTooltip";
import { Dropdown, SearchInput } from "../../common_components/FormInputs";
import { toast } from "../../common_components/Toast/ToastUtils";
import { selectCanManageReports, selectCanManageAutoSchedule, selectEntityType, selectIsSuperAdmin, selectToken } from "../../redux/authSlice";
import { setActiveModuleKey } from "../../redux/userDataSlice";
import { fetchActiveClients, selectActiveClients, selectUtilityLoading, } from "../../redux/utilitySlice";
import { ROUTES } from "../../routes/routes.constants";
import reportBuilderService from "../../services/reportBuilder.service";
import RowActions from "./components/RowActions";
import CloneReportModal, { useCloneReport } from "./components/CloneReportModal";
import ReportActionDialog, { DialogNote } from "./components/ReportActionDialog";
import ScheduleReportModal from "./components/ScheduleReportModal";
/**
 * Create Report - saved reports listing.
 *
 * Data: GET /api/report-builder/reports. The server applies client_scope; the
 * Store options come from fetchActiveClients, the same list the client header
 * (useClientSelection) shows. "Manage Reports" only decides
 * whether Add / Edit are offered.
 */
const MODULE_KEY = "create_report";
const ITEMS_PER_PAGE = 10;
// Table column id → the API's sort_by. "Created by" shows the creator with the
// creation date under it and sorts on that date (newest first by default).
const SORT_KEYS = {
    report_name: "report_name",
    created_by: "created_at",
};
// MM/DD/YYYY in US Eastern, matching the rest of the portal.
const DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
});
// ── Schedule column ──
const SCHEDULE_FREQUENCY_LABELS = {
    day: "Daily",
    week: "Weekly",
    bi_weekly: "Bi-Weekly",
    month: "Monthly",
};
const SCHEDULE_WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
/** "09:00" → "9:00 AM" - the saved time is already in the schedule's zone. */
const fmtScheduleTime = (hhmm) => {
    const [h, m] = hhmm.split(":").map(Number);
    return `${h % 12 || 12}:${String(m || 0).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};
const ordinal = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};
/** Approved listing format: "Daily · 1:00 AM", "Weekly · Mon 9:00 AM", "Monthly · 1st, 9:00 AM". */
const fmtScheduleRule = (s) => {
    const time = fmtScheduleTime(s.time);
    const when = s.frequency === "week" || s.frequency === "bi_weekly"
        ? `${s.weekdays.map((d) => SCHEDULE_WEEKDAYS[d]).join(", ")} ${time}`
        : s.frequency === "month"
            ? `${s.month_days.map(ordinal).join(", ")}, ${time}`
            : time;
    return `${SCHEDULE_FREQUENCY_LABELS[s.frequency]} · ${when}`;
};
/** UTC next_run_at → "10/03/2026, 9:00 AM" in the schedule's zone (not the browser's). */
const fmtNextRun = (iso, timeZone) => {
    if (!iso)
        return null;
    const d = new Date(iso);
    if (isNaN(d.getTime()))
        return null;
    return new Intl.DateTimeFormat("en-US", {
        timeZone,
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
    }).format(d);
};
/** Schedule column hover - next run (Eastern) and recipient counts, in the portal's dark tooltip style. */
const ScheduleTooltipContent = ({ schedule: s }) => {
    const next = fmtNextRun(s.next_run_at, s.timezone);
    const total = s.internal_count + s.external_count + s.additional_external_count;
    const recipients = [
        { label: "Internal", count: s.internal_count, color: "#2E90FA" },
        { label: "External", count: s.external_count, color: "#12B76A" },
        { label: "Additional External", count: s.additional_external_count, color: "#F79009" },
    ];
    return (_jsxs(Box, { sx: { p: 0.5, minWidth: 230, color: "#fff" }, children: [_jsxs(Box, { sx: {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                    pb: 0.5,
                    borderBottom: "1px solid rgba(255, 255, 255, 0.15)",
                }, children: [_jsx(Typography, { sx: { fontSize: "11px", fontWeight: 700, color: "#9ca3af", letterSpacing: "0.5px" }, children: "NEXT RUN" }), _jsx(Typography, { sx: { fontSize: "11px", fontWeight: 500, color: "#9ca3af" }, children: "Eastern Time" })] }), _jsx(Typography, { sx: { fontSize: "13px", fontWeight: 600, color: "#fff", mb: 1.5 }, children: next ? `${next} ET` : "Not due" }), _jsxs(Box, { sx: {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                    pb: 0.5,
                    borderBottom: "1px solid rgba(255, 255, 255, 0.15)",
                }, children: [_jsx(Typography, { sx: { fontSize: "11px", fontWeight: 700, color: "#9ca3af", letterSpacing: "0.5px" }, children: "ASSIGNED RECIPIENTS" }), _jsxs(Typography, { sx: { fontSize: "11px", fontWeight: 500, color: "#9ca3af" }, children: [total, " recipient", total === 1 ? "" : "s"] })] }), _jsx(Box, { sx: { display: "flex", flexDirection: "column", gap: 0.75 }, children: recipients.map((r) => (_jsxs(Box, { sx: { display: "flex", justifyContent: "space-between", alignItems: "center" }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [_jsx(Box, { sx: { width: 7, height: 7, borderRadius: "50%", backgroundColor: r.color } }), _jsx(Typography, { sx: { fontSize: "12px", color: "#d1d5db" }, children: r.label })] }), _jsx(Typography, { sx: { fontSize: "12px", fontWeight: 600, color: "#fff" }, children: r.count })] }, r.label))) })] }));
};
const fmtDate = (iso) => {
    if (!iso)
        return "—";
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "—" : DATE_FORMATTER.format(d);
};
/** Report name is held at exactly this width; longer names are cut with "..." and shown in a tooltip. */
const REPORT_NAME_WIDTH = "200px";
const reportNameCellSx = {
    width: REPORT_NAME_WIDTH,
    minWidth: REPORT_NAME_WIDTH,
    maxWidth: REPORT_NAME_WIDTH,
    overflow: "hidden",
};
/** Report name with an ellipsis; the tooltip appears only when the name is actually cut off. */
const ReportNameCell = ({ name }) => {
    const [truncated, setTruncated] = useState(false);
    return (_jsx(HoverTooltip, { title: truncated ? name : "", placement: "top", arrow: true, children: _jsx(Typography, { onMouseEnter: (e) => setTruncated(e.currentTarget.scrollWidth > e.currentTarget.clientWidth), sx: {
                fontSize: "14px !important",
                // fontWeight: 600,
                color: "#101828",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            }, children: name }) }));
};
const COLUMNS = [
    {
        id: "report_name",
        label: "Report name",
        width: REPORT_NAME_WIDTH,
        sortable: true,
        align: "left",
        headerSx: reportNameCellSx,
        cellSx: () => reportNameCellSx,
    },
    { id: "client_name", label: "Client", width: "160px", sortable: false, align: "left" },
    { id: "dimensions", label: "Dimension", width: "240px", sortable: false, align: "left" },
    { id: "date_range", label: "Date range", width: "200px", sortable: false, align: "left" },
    { id: "schedule", label: "Schedule", width: "180px", sortable: false, align: "left" },
    { id: "created_by", label: "Created by", width: "180px", sortable: true, align: "left" },
    { id: "action", label: "", width: "80px", sortable: false, align: "center" },
];
// Clients see no Schedule or Created by - both are internal.
const CLIENT_COLUMNS = COLUMNS.filter((c) => c.id !== "schedule" && c.id !== "created_by");
const labeledValue = (label, value) => (_jsxs("span", { children: [_jsxs("span", { style: { color: "#101828", fontWeight: 500 }, children: [label, ":"] }), " ", _jsx("span", { style: { color: "#667085" }, children: value })] }));
const textSx = { fontSize: "14px !important", color: "#344054" };
const CreateReportList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const token = useSelector(selectToken) || "";
    const hasManageReports = useSelector(selectCanManageReports);
    const hasManageAutoSchedule = useSelector(selectCanManageAutoSchedule);
    const isSuperAdmin = useSelector(selectIsSuperAdmin);
    // Client contacts are view-only: sent reports only, no filters, no actions
    // beyond View.
    const isClient = useSelector(selectEntityType) === "client";
    const canManage = !isClient && (isSuperAdmin || hasManageReports);
    const canManageAutoSchedule = !isClient && (isSuperAdmin || hasManageAutoSchedule);
    // ── Filters / paging ────────────────────────────────────────────────
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedClientId, setSelectedClientId] = useState("");
    const [selectedUserId, setSelectedUserId] = useState("");
    const [sortConfig, setSortConfig] = useState({
        columnId: "created_by",
        direction: "desc",
    });
    const [currentPage, setCurrentPage] = useState(1);
    // ── Data ────────────────────────────────────────────────────────────
    /** `id` is aliased from `report_id` so CommonTable has a stable row key. */
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const isFirstLoad = useRef(true);
    // ── Filter options ──────────────────────────────────────────────────
    // Store options come from the same place as useClientSelection /
    // ClientGroupHeader: utilitySlice's fetchActiveClients for this module.
    const activeClients = useSelector(selectActiveClients);
    const clientsLoading = useSelector(selectUtilityLoading);
    const [creators, setCreators] = useState([]);
    const [creatorsLoading, setCreatorsLoading] = useState(true);
    // setActiveModuleKey is a plain action, so the module key is in the store
    // before the fetches below read it for the x-module-key header.
    useEffect(() => {
        dispatch(setActiveModuleKey(MODULE_KEY));
        dispatch(fetchActiveClients({ status: "active", moduleName: MODULE_KEY }));
    }, [dispatch]);
    useEffect(() => {
        // Clients get no User filter, and the API refuses them this list.
        if (isClient) {
            setCreatorsLoading(false);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const res = await reportBuilderService.listReportCreators(token);
                if (!cancelled && res?.data?.success)
                    setCreators(res.data.data || []);
            }
            finally {
                if (!cancelled)
                    setCreatorsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [token, isClient]);
    // Same options as useClientSelection: each client followed by its group
    // accounts, clients sorted by name.
    const clientOptions = useMemo(() => {
        const list = Array.isArray(activeClients)
            ? activeClients
            : activeClients?.data || [];
        const options = [{ value: "", label: "All" }];
        [...list]
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
            .forEach((c) => {
            options.push({ value: c._id, label: c.name });
            (Array.isArray(c.group_accounts) ? c.group_accounts : []).forEach((ga) => {
                options.push({ value: ga._id, label: ga.name });
            });
        });
        return options;
    }, [activeClients]);
    const userOptions = useMemo(() => [
        { value: "", label: "All" },
        ...creators.map((u) => ({ value: u.user_id, label: u.name || u.email })),
    ], [creators]);
    const labelFor = (options, value) => options.find((o) => o.value === value)?.label || options[0].label;
    // ── Load ────────────────────────────────────────────────────────────
    const loadReports = useCallback(async () => {
        setLoading(true);
        try {
            const res = await reportBuilderService.listReports({
                page: currentPage,
                limit: ITEMS_PER_PAGE,
                search: searchTerm || undefined,
                client_id: selectedClientId || undefined,
                created_by: selectedUserId || undefined,
                sort_by: sortConfig ? SORT_KEYS[sortConfig.columnId] : undefined,
                sort_order: sortConfig?.direction,
            }, token);
            if (!res?.data?.success) {
                throw new Error(res?.data?.message || res?.response?.data?.message);
            }
            const data = res.data.data;
            // CommonTable keys rows off `row.id`, falling back to the row index —
            // aliasing report_id keeps keys stable across sorts and pages.
            setRows((data.items || []).map((r) => ({ ...r, id: r.report_id })));
            setTotal(data.total || 0);
        }
        catch (err) {
            setRows([]);
            setTotal(0);
            toast.error(err?.message || "Failed to fetch reports.");
        }
        finally {
            isFirstLoad.current = false;
            setLoading(false);
        }
    }, [token, currentPage, searchTerm, selectedClientId, selectedUserId, sortConfig]);
    useEffect(() => {
        loadReports();
    }, [loadReports]);
    // ── Handlers ────────────────────────────────────────────────────────
    const handleSortChange = useCallback((columnId, direction) => {
        setCurrentPage(1);
        setSortConfig(direction ? { columnId, direction } : null);
    }, []);
    const handleSearch = useCallback((value) => {
        setSearchTerm(value.trim());
        setCurrentPage(1);
    }, []);
    const handleFilter = useCallback((setter) => (value) => {
        setter(value);
        setCurrentPage(1);
    }, []);
    // Add / Edit / View navigate to their own routes.
    const handleAddNew = () => navigate(ROUTES.createReportAdd.path);
    const handleEdit = (row) => navigate(ROUTES.createReportEdit.path.replace(":reportId", row.report_id));
    const handleView = (row) => navigate(ROUTES.createReportView.path.replace(":reportId", row.report_id));
    // ── Clone ───────────────────────────────────────────────────────────
    // Same dialog + API as the Edit page; the copy opens on Edit.
    const clone = useCloneReport();
    /** The dialog assigns a copy to one client, so "All Clients" is not an option. */
    const cloneClientOptions = useMemo(() => clientOptions.filter((opt) => opt.value), [clientOptions]);
    // ── Delete ──────────────────────────────────────────────────────────
    /** Report awaiting the confirm dialog; null when it is closed. */
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const handleDeleteConfirm = async () => {
        if (!deleteTarget)
            return;
        setDeleting(true);
        try {
            const res = await reportBuilderService.deleteReport(deleteTarget.report_id, token);
            if (!res?.data?.success) {
                throw new Error(res?.data?.message || res?.response?.data?.message);
            }
            toast.success(res.data.message || "Report deleted.");
            setDeleteTarget(null);
            // The last row of a page going away would leave an empty page —
            // step back one; changing the page reloads by itself.
            if (rows.length === 1 && currentPage > 1)
                setCurrentPage(currentPage - 1);
            else
                loadReports();
        }
        catch (err) {
            toast.error(err?.message || "Could not delete the report. Please try again.");
        }
        finally {
            setDeleting(false);
            setDeleteTarget(null);
        }
    };
    // ── Send ────────────────────────────────────────────────────────────
    /** Report awaiting the send confirm dialog; null when it is closed. */
    const [sendTarget, setSendTarget] = useState(null);
    const [sending, setSending] = useState(false);
    const handleSendConfirm = async () => {
        if (!sendTarget)
            return;
        setSending(true);
        try {
            const res = await reportBuilderService.sendReport(sendTarget.report_id, token);
            if (!res?.data?.success) {
                throw new Error(res?.data?.message || res?.response?.data?.message);
            }
            toast.success(res.data.message || "Report has been sent successfully.");
            setSendTarget(null);
        }
        catch (err) {
            toast.error(err?.message || "Could not send the report. Please try again.");
        }
        finally {
            setSending(false);
        }
    };
    // ── Schedule ────────────────────────────────────────────────────────
    /** Report open in the Schedule modal; null when it is closed. */
    const [scheduleTarget, setScheduleTarget] = useState(null);
    /** Additional Recipients - the same active-user list as the User filter. */
    const recipientOptions = useMemo(() => creators.map((u) => ({
        value: u.user_id,
        label: u.name || u.email,
        description: u.name ? u.email : undefined,
    })), [creators]);
    const [scheduling, setScheduling] = useState(false);
    const handleScheduleSubmit = async (submission) => {
        if (!scheduleTarget || !canManageAutoSchedule)
            return;
        setScheduling(true);
        try {
            const res = await reportBuilderService.saveReportSchedule(scheduleTarget.report_id, { ...submission }, token);
            if (!res?.data?.success) {
                throw new Error(res?.data?.message || res?.response?.data?.message);
            }
            toast.success(res.data.message || "Report has been scheduled.");
            loadReports();
            setScheduleTarget(null);
        }
        catch (err) {
            toast.error(err?.message || "Could not save the schedule. Please try again.");
        }
        finally {
            setScheduling(false);
        }
    };
    const [deletingSchedule, setDeletingSchedule] = useState(false);
    const handleScheduleDelete = async () => {
        if (!scheduleTarget || !canManageAutoSchedule)
            return;
        setDeletingSchedule(true);
        try {
            const res = await reportBuilderService.deleteReportSchedule(scheduleTarget.report_id, token);
            if (!res?.data?.success) {
                throw new Error(res?.data?.message || res?.response?.data?.message);
            }
            toast.success(res.data.message || "Schedule deleted.");
            loadReports();
            setScheduleTarget(null);
        }
        catch (err) {
            toast.error(err?.message || "Could not delete the schedule. Please try again.");
        }
        finally {
            setDeletingSchedule(false);
        }
    };
    // ── Cell renderer ───────────────────────────────────────────────────
    const renderCell = (row, col) => {
        switch (col.id) {
            case "report_name":
                return _jsx(ReportNameCell, { name: row.report_name });
            case "client_name":
                return _jsx(Typography, { sx: textSx, children: row.client_name || "—" });
            case "dimensions":
                if (!row.dimensions?.length)
                    return _jsx(Typography, { sx: textSx, children: "\u2014" });
                return (_jsx(Box, { sx: { display: "flex", flexWrap: "wrap", gap: 0.75 }, children: row.dimensions.map((label) => (_jsx(Box, { sx: {
                            px: 1,
                            py: 0.25,
                            borderRadius: "6px",
                            bgcolor: "#F2F4F7",
                            color: "#344054",
                            fontSize: "12px !important",
                            fontWeight: "700 !important",
                            whiteSpace: "nowrap",
                        }, children: label }, label))) }));
            case "date_range":
                // Comparison ON → main period on top, compare period beneath.
                return (_jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: 0.25 }, children: [_jsx(Typography, { sx: { ...textSx, whiteSpace: "nowrap" }, children: row.date_range }), row.compare_date_range && (_jsx(Typography, { title: "Comparison period", sx: { fontSize: "12px !important", color: "#667085", whiteSpace: "nowrap" }, children: row.compare_date_range }))] }));
            case "schedule": {
                const s = row.scheduled;
                if (!s)
                    return _jsx(Typography, { sx: textSx, children: "Not Scheduled" });
                // One line per the approved design; next run + recipients on hover.
                return (_jsx(HoverTooltip, { title: _jsx(ScheduleTooltipContent, { schedule: s }), placement: "top", arrow: true, children: _jsx(Typography, { sx: {
                            ...textSx,
                            display: "inline-block",
                            maxWidth: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            cursor: "default",
                        }, children: fmtScheduleRule(s) }) }));
            }
            case "created_by":
                return (_jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }, children: [_jsx(Typography, { sx: {
                                fontSize: "14px !important",
                                color: "#101828",
                                fontWeight: 500,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }, title: row.created_by_name, children: row.created_by_name || "—" }), _jsx(Typography, { sx: { fontSize: "12px !important", color: "#667085" }, children: fmtDate(row.created_at) })] }));
            case "action":
                return (_jsx(RowActions, { row: row, canManage: canManage, canManageAutoSchedule: canManageAutoSchedule, onView: handleView, onEdit: handleEdit, onDelete: setDeleteTarget, onClone: clone.open, onSend: setSendTarget, onSchedule: setScheduleTarget }));
            default:
                return null;
        }
    };
    const hasFilters = !!(searchTerm || selectedClientId || selectedUserId);
    // ── Render ──────────────────────────────────────────────────────────
    return (_jsxs(PageContainer, { children: [_jsxs(Box, { sx: {
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    height: "calc(100vh - 90px)",
                    overflow: "hidden",
                }, children: [_jsx(PagesCommonHeader, { height: "58px", leftSection: _jsx(Box, { sx: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5 }, children: !isClient && (_jsxs(_Fragment, { children: [_jsx(Dropdown, { value: selectedClientId, onChange: handleFilter(setSelectedClientId), options: clientOptions, placeholder: "Client: All", displayValue: labeledValue("Client", labelFor(clientOptions, selectedClientId)), leftIcon: "/assets/userC.svg", size: "sm", fullWidth: false, className: "header-dropdown", 
                                        // loading={clientsLoading}
                                        searchable: clientOptions.length > 10 }), _jsx(Dropdown, { value: selectedUserId, onChange: handleFilter(setSelectedUserId), options: userOptions, placeholder: "User: All", displayValue: labeledValue("User", labelFor(userOptions, selectedUserId)), leftIcon: "/assets/userC.svg", size: "sm", fullWidth: false, className: "header-dropdown", 
                                        // loading={creatorsLoading}
                                        searchable: userOptions.length > 10 })] })) }), rightSection: _jsxs(_Fragment, { children: [_jsx(Box, { sx: { width: 280 }, children: _jsx(SearchInput, { placeholder: "Search reports", onSearch: handleSearch, size: "sm", debounceMs: 400 }) }), canManage && (_jsx(AppButton, { variant: "contained", startIcon: _jsx(Plus, { size: 16 }), onClick: handleAddNew, className: "all-Font-Sizes", sx: {
                                        bgcolor: "#FF008E",
                                        textTransform: "none",
                                        whiteSpace: "nowrap",
                                        "&:hover": { bgcolor: "#E6007A" },
                                    }, children: "Add Report" }))] }) }), _jsxs(Paper, { elevation: 0, sx: {
                            border: "1px solid #D0D5DD",
                            borderRadius: 2,
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                            flex: 1,
                            minHeight: 0,
                        }, children: [_jsx(Box, { sx: {
                                    overflow: "hidden",
                                    flex: 1,
                                    minHeight: 0,
                                    // Give the table's scroll container a definite height so its
                                    // maxHeight:100% resolves and the body scrolls internally,
                                    // keeping the header row sticky.
                                    "& .reusable-table-container": { height: "100%" },
                                }, children: _jsx(CommonTable, { columns: isClient ? CLIENT_COLUMNS : COLUMNS, rows: rows, renderCell: renderCell, sortConfig: sortConfig, onSortChange: handleSortChange, isLoading: loading, isFirstLoad: isFirstLoad.current, emptyMessage: hasFilters
                                        ? "No reports match your filters."
                                        : isClient
                                            ? "No reports have been shared with you yet."
                                            : 'No saved reports yet - click "Add Report" to build the first one.', stickyHeader: true, expandTrigger: "none", maxHeight: "100%", pinnedColumns: { left: [], right: ["action"] } }) }), !loading && total > 0 && (_jsx(Box, { sx: { p: 2, borderTop: "1px solid #EAECF0", flexShrink: 0 }, children: _jsx(Pagination, { currentPage: currentPage, totalPages: Math.ceil(total / ITEMS_PER_PAGE), onPageChange: setCurrentPage, maxVisiblePages: 5, totalEntries: total, itemsPerPage: ITEMS_PER_PAGE, showEntriesMessage: true }) }))] })] }), _jsx(ConfirmActionModal, { open: Boolean(deleteTarget), title: "Delete report", description: `"${deleteTarget?.report_name ?? ""}" will be deleted permanently, This cannot be undone.`, confirmText: "Delete", isDeleteIcon: true, loading: deleting, onConfirm: handleDeleteConfirm, onClose: () => setDeleteTarget(null) }), _jsx(ReportActionDialog, { open: Boolean(sendTarget), title: "Send Report", confirmText: "Send Report", loading: sending, onConfirm: handleSendConfirm, onClose: () => setSendTarget(null), children: _jsxs(DialogNote, { children: ["Are you sure you want to Send ", _jsx("strong", { children: sendTarget?.report_name ?? "" }), " report to the client?"] }) }), _jsx(CloneReportModal, { ...clone.modalProps, clientOptions: cloneClientOptions, clientsLoading: clientsLoading }), _jsx(ScheduleReportModal, { open: Boolean(scheduleTarget), target: scheduleTarget, userOptions: recipientOptions, loading: scheduling, deleting: deletingSchedule, onSubmit: handleScheduleSubmit, onDelete: handleScheduleDelete, onClose: () => setScheduleTarget(null) })] }));
};
export default CreateReportList;
